import { RemoteData } from './remote-data'
import { Paginated } from '@/modules/shared/models/data'

type PaginationProps = {
  total: number
  perPage: number
  nextPage: number | null
  lastPage: number
  currentPage: number
  lastIndex: number
}

export class Paginatable<T extends Paginated<any>, P = any> extends RemoteData<T> implements PaginationProps {
  public s = ''
  public restParams: any = null
  public currentPage: number = 0
  public total = 0
  public perPage = 0
  public lastPage = 0
  public nextPage: number | null = 0
  public lastIndex = 0

  constructor(
    private readonly fetch: (page: number, search: string, params?: P) => RemoteData<T>,
    private readonly mergeStrategy
      : (oldValue: T, newValue: T) => T = (oldValue, newValue) => newValue
  ) {
    super(RemoteData.states.NotAsked)
  }

  private getPaginationProps(data: T): PaginationProps {
    const lastPage = data.meta.last_page
    const currentPage = data.meta.current_page
    return {
      currentPage,
      nextPage: currentPage >= lastPage ? null : currentPage + 1,
      perPage: data.meta.per_page,
      total: data.meta.total,
      lastIndex: (data.meta.per_page * data.meta.current_page) - 1,
      lastPage
    }
  }

  private setPaginationProps(props: PaginationProps) {
    this.currentPage = props.currentPage
    this.total = props.total
    this.perPage = props.perPage
    this.lastPage = props.lastPage
    this.nextPage = props.nextPage
    this.lastIndex = props.lastIndex
  }

  success(data: T) {
    super.success(
      this.value
        ? this.mergeStrategy(this.value as T, data)
        : data
    )
    return this
  }

  next(params?: P) {
    this.fromRemoteData(
      this.fetch(this.currentPage + 1, this.s, params)
    )
      .then(() => {
        this.setPaginationProps(
          this.getPaginationProps(this.value as T)
        )
      })
    return this
  }

  previous(params?: P) {
    if (this.currentPage < 1) {
      throw new Error('Can\'t go below zero')
    }
    this.fromRemoteData(
      this.fetch(this.currentPage - 1, this.s, params)
    ).then(() => {
      this.setPaginationProps(
        this.getPaginationProps(this.value as T)
      )
    })
    return this
  }

  goToPage(pageNumber: number, params?: P) {
    this.fromRemoteData(
      this.fetch(pageNumber, this.s, params)
    ).then(() => {
      this.setPaginationProps(
        this.getPaginationProps(this.value as T)
      )
    })
    return this
  }

  search(s: string, params?: P) {
    if (this.s === s) return this
    this.inProgress()
    this.fromRemoteData(
      this.fetch(1, s, params)
    ).then(() => {
      this.s = s
      this.setPaginationProps(
        this.getPaginationProps(this.value as T)
      )
    })
    return this
  }

  params(params: P) {
    this.inProgress()

    this.fromRemoteData(
      this.fetch(1, this.s, params)
    ).then(() => {
      this.setPaginationProps(
        this.getPaginationProps(this.value as T)
      )
    })

    return this
  }

  refresh() {
    this.goToPage(this.currentPage)

    return this
  }
}
